import React from "react";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BadgeCheck } from "lucide-react";
import { Plan, CurrentPlan } from "@/types/plans";
import {
  isCurrentPlan,
  isPlanSelectable,
  formatFeatureName,
  formatFeatureValue,
} from "./planUtils";
import { PlanIcon } from "./PlanIcons";
import { PlanPrice } from "./PlanPrice";

interface PlanCardProps {
  plan: Plan;
  isYearly: boolean;
  currentPlan: CurrentPlan | null;
  loading: string | null;
  onSelect: (plan: Plan) => void;
  showPricing: boolean;
}

export const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  isYearly,
  currentPlan,
  loading,
  onSelect,
  showPricing,
}) => {
  const isPopular = plan.name.toLowerCase() === "growth";
  const isEnterprise = plan.name.toLowerCase() === "enterprise";
  const isFree = plan.name.toLowerCase() === "free";
  const isPlanCurrent = isCurrentPlan(currentPlan, plan, isYearly);
  const isSelectable = isPlanSelectable(currentPlan, plan, isYearly);

  const getButtonText = (): React.ReactNode => {
    if (loading === plan.id)
      return (
        <>
          <Spinner className="h-3 w-3" />
          <span>Loading...</span>
        </>
      );
    if (!isSelectable) return "Not Available";
    if (isFree) return "Start with Free Plan";
    if (!showPricing && !isFree) return "Contact Sales";
    if (isEnterprise) return "Contact Sales";
    if (currentPlan) return "Change Plan";
    return "Get Started";
  };

  const getButtonStyle = (): string => {
    if (!isSelectable)
      return "bg-muted text-muted-foreground cursor-not-allowed";
    if (isFree)
      return "bg-background border text-foreground hover:bg-accent hover:text-foreground";
    if (isPopular) return "";
    if (isEnterprise) return "bg-purple-600 hover:bg-purple-700 text-white";
    return "";
  };

  return (
    <div className="relative">
      <div
        className={`
          relative z-10 mt-0 block h-full w-64 overflow-hidden
          transition-all duration-180 ease-in-out
          rounded-lg rounded-tr-[min(var(--radius-4xl),26px)] bg-background px-4 pt-5 pb-[18px]
          shadow-[inset_0_0_0_1px] shadow-border
          before:absolute before:top-0 before:right-0 before:z-3 before:h-[30px] before:w-[30px]
          before:-translate-y-1/2 before:translate-x-1/2 before:rotate-45
          before:bg-background
          before:shadow-[0_1px_0_0] before:shadow-border
          before:transition-all before:duration-180 before:ease-in-out before:content-['']
          after:absolute after:top-0 after:right-0 after:z-2 after:size-7
          after:-translate-y-2 after:translate-x-2 after:rounded-bl-[min(var(--radius-lg),10px)]
          after:border after:border-dashed after:border-border
          after:bg-muted
          after:shadow-xs after:transition-all after:duration-180 after:ease-in-out after:content-['']
          hover:rounded-tr-[min(var(--radius-4xl)*1.75,45px)] hover:before:h-[50px] hover:before:w-[50px]
          hover:after:h-[42px] hover:after:w-[42px] hover:after:shadow-lg hover:after:shadow-black/5
          ${!isSelectable ? "opacity-60 cursor-not-allowed" : ""}
          ${isPopular ? "" : ""}
        `}
      >
        <div className="relative flex h-full flex-col">
          {/* Header Section */}
          <div className="relative mb-6 flex flex-col items-center justify-center border-b border-border pb-6">
            {isPopular && (
              <div className="absolute top-1/6 -left-4 h-1/2 w-[4px] rounded-r-sm bg-primary" />
            )}
            <div className="flex items-center justify-center space-x-2 mb-3">
              <PlanIcon planName={plan.name} />
              <span
                className={`text-lg font-semibold ${isPopular
                    ? "text-primary"
                    : isEnterprise
                      ? "text-purple-600"
                      : "text-foreground"
                  }`}
              >
                {plan.name}
              </span>
            </div>
            <PlanPrice plan={plan} isYearly={isYearly} showPricing={showPricing} />
          </div>

          {/* Features Section */}
          <div className="mb-5 flex-1 space-y-2.5">
            {Object.entries(plan.features).map(([featureName, feature]) => (
              <div key={featureName} className="flex items-start gap-2">
                <BadgeCheck
                  className={`h-4 w-4 mt-1 ${isPopular
                      ? "text-primary"
                      : isEnterprise
                        ? "text-purple-600"
                        : "text-foreground"
                    }`}
                />
                <div className="flex-1 items-start h-full">
                  <p className="text-sm font-medium text-foreground">
                    {formatFeatureName(featureName, plan?.name === "Free")}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatFeatureValue(
                      feature,
                      featureName == "activated_creators" ||
                      featureName == "creator_outreach"
                    )}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          {isPlanCurrent ? (
            <Badge
              variant="secondary"
              className="w-full h-9 justify-center font-medium text-sm"
            >
              Current Plan
            </Badge>
          ) : (
            <Button
              onClick={() => isSelectable && onSelect(plan)}
              disabled={loading === plan.id || !isSelectable}
              className={`${getButtonStyle()}`}
              variant="default"
            >
              {getButtonText()}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
